import { useEffect, useMemo, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { AlertCircle, BarChart3, Loader2, RefreshCw } from 'lucide-react'
import { AxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PowerBiReportEmbed } from '@/components/common/PowerBiReportEmbed'
import { type PowerBiDashboardType } from '@/api/powerbi'
import { POWERBI_REPORTS, WORKSPACE_LABELS } from '@/config/powerbiReports'
import { usePowerBiDashboard } from '@/hooks/usePowerBiDashboard'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useMyDashboardAccess } from '@/hooks/useReportPermissions'
import { makePowerBiFilterKey } from '@/stores/usePowerBiFilterStore'
import { toast } from '@/stores/useToastStore'

const EVENT_PRODUCT_FLOW_REPORT_ID = '85c7eabb-4095-4f6e-8c26-f8bf820a6a30'

function getApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const detail = error.response?.data?.detail
    if (typeof detail === 'string' && detail.trim()) return detail
  }
  if (error instanceof Error) return error.message
  return 'Failed to load Power BI dashboard'
}

export default function Dashboard() {
  const { workspace } = useParams<{ workspace: string }>()
  const selectedDashboard: PowerBiDashboardType =
    workspace === 'flow-tool' ? 'IFP' : 'CAT'

  const [searchParams, setSearchParams] = useSearchParams()

  const {
    data: access,
    isLoading: isCheckingPermissions,
    isSuccess: permissionsLoaded,
  } = useMyDashboardAccess()

  const reports = useMemo(() => {
    const catalog = POWERBI_REPORTS[selectedDashboard]
    if (!permissionsLoaded || !access) return []
    let allowed: typeof catalog
    if (access.is_admin) {
      allowed = [...catalog]
    } else if (selectedDashboard === 'CAT') {
      allowed = access.cat_access ? [...catalog] : []
    } else {
      const ifpIds = new Set(access.ifp_report_ids)
      allowed = catalog.filter(r => ifpIds.has(r.reportId))
    }
    // Pin Event Product Flow first when present.
    const eventIdx = allowed.findIndex(
      r => r.reportId === EVENT_PRODUCT_FLOW_REPORT_ID
    )
    if (eventIdx > 0) {
      const [event] = allowed.splice(eventIdx, 1)
      allowed.unshift(event)
    }
    return allowed
  }, [access, permissionsLoaded, selectedDashboard])

  const reportParam = searchParams.get('report')
  const isParamValid =
    reportParam !== null && reports.some(r => r.reportId === reportParam)
  const selectedReportId: string | null = isParamValid
    ? reportParam
    : (reports[0]?.reportId ?? null)

  const lastToastMessageRef = useRef<string | null>(null)

  const {
    data: embedConfig,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = usePowerBiDashboard(selectedReportId, selectedDashboard, {
    enabled: permissionsLoaded && selectedReportId !== null,
  })

  const { userId } = useCurrentUser()
  const persistenceKey = makePowerBiFilterKey(
    userId,
    selectedDashboard,
    selectedReportId
  )

  useEffect(() => {
    if (!isError) return

    const message = getApiErrorMessage(error)

    if (lastToastMessageRef.current !== message) {
      lastToastMessageRef.current = message
      toast.error(message)
    }
  }, [error, isError])

  useEffect(() => {
    lastToastMessageRef.current = null
  }, [selectedDashboard])

  useEffect(() => {
    if (!permissionsLoaded) return
    if (isParamValid) return

    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (selectedReportId) {
          next.set('report', selectedReportId)
        } else {
          next.delete('report')
        }
        return next
      },
      { replace: true }
    )
  }, [isParamValid, permissionsLoaded, selectedReportId, setSearchParams])

  const handleRefresh = () => {
    lastToastMessageRef.current = null
    if (!selectedReportId) return
    void refetch()
  }

  const hasReports = reports.length > 0

  const showErrorState =
    hasReports &&
    selectedReportId != null &&
    !isLoading &&
    !isFetching &&
    (isError || !embedConfig)

  if (isCheckingPermissions) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        {hasReports ? (
          <Select
            value={selectedReportId ?? undefined}
            onValueChange={value => {
              setSearchParams(
                prev => {
                  const next = new URLSearchParams(prev)
                  next.set('report', value)
                  return next
                },
                { replace: true }
              )
              lastToastMessageRef.current = null
            }}
          >
            <SelectTrigger className="w-auto gap-2 border-none bg-transparent p-0 text-2xl font-semibold text-foreground shadow-none focus:ring-0 cursor-pointer">
              <SelectValue placeholder="Select a report" />
            </SelectTrigger>
            <SelectContent>
              {reports.map(report => (
                <SelectItem
                  key={report.reportId}
                  value={report.reportId}
                  className="cursor-pointer"
                >
                  {report.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <h1 className="text-2xl font-semibold text-foreground">
            {WORKSPACE_LABELS[selectedDashboard]} Dashboard
          </h1>
        )}

        <Button
          type="button"
          variant="outline"
          onClick={handleRefresh}
          disabled={isFetching || !selectedReportId}
          className="border-[#556374]"
        >
          <RefreshCw
            className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          {!hasReports ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <BarChart3 className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-foreground">
                  No reports available
                </h2>
                <p className="max-w-xl text-sm text-muted-foreground">
                  You don&apos;t have access to any{' '}
                  {WORKSPACE_LABELS[selectedDashboard]} reports yet. Contact an
                  administrator to request access.
                </p>
              </div>
            </div>
          ) : showErrorState ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-foreground">
                  Unable to load dashboard
                </h2>
                <p className="max-w-xl text-sm text-muted-foreground">
                  {getApiErrorMessage(error)}
                </p>
              </div>
              <Button
                type="button"
                onClick={handleRefresh}
                disabled={isFetching}
              >
                Try again
              </Button>
            </div>
          ) : embedConfig ? (
            <PowerBiReportEmbed
              embedConfig={embedConfig}
              reportType={selectedDashboard}
              pageName={
                reports.find(r => r.reportId === selectedReportId)?.pageId
              }
              reportLabel={
                reports.find(r => r.reportId === selectedReportId)?.label
              }
              loadingLabel={`${WORKSPACE_LABELS[selectedDashboard]} dashboard`}
              onRefresh={handleRefresh}
              isFetching={isFetching}
              persistenceKey={persistenceKey}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
